<template>
  <!-- リクエスト予約 -->
  <div v-if="isRequestReservation" class="flex flex-col gap-y-4 bg-white px-4 py-6 font-Spectral text-sm md:px-10 md:py-8 md:text-base">
    <div class="flex items-center gap-x-2 text-gold3">
      <span class="material-symbols-sharp text-[40px] leading-none">hourglass_empty</span>
      <p class="text-[18px] font-medium tracking-[0.5px] md:text-[22px]">Request In Review</p>
    </div>

    <ol class="reservation-wizard flex justify-between">
      <li class="reservation-wizard__list reservation-wizard__list--active before:!border-gold1 before:[&:nth-child(n+1)]:border-t-[1px]">
        <p class="m-auto max-w-[70px] pt-3 text-xs leading-4 tracking-[0.5px] text-gold3">Request sent</p>
      </li>
      <li class="reservation-wizard__list before:border-gray1 before:[&:nth-child(n+1)]:border-t-[1px]">
        <p class="m-auto max-w-[70px] pt-3 text-xs leading-4 tracking-[0.5px]">Under review</p>
      </li>
      <li class="reservation-wizard__list before:border-gray1 before:[&:nth-child(n+1)]:border-t-[1px]">
        <p class="m-auto max-w-[70px] pt-3 text-xs leading-4 tracking-[0.5px]">Booking confirmed</p>
      </li>
    </ol>

    <div class="flex gap-x-2 bg-red-100 p-4">
      <span class="material-symbols-sharp shrink-0 text-[24px] leading-none text-red1">warning</span>
      <div class="leading-6">
        <p class="font-bold">This booking is not yet confirmed.</p>
        <p>You will receive a separate "Booking Confirmation" email once approved.</p>
      </div>
    </div>

    <div class="leading-6 text-gray6">
      <p class="mb-1">Your booking request has been received. We are now confirming availability with the host, which may take 2-4 business days, as each experience is individually arranged.</p>
      <p class="mask-me">An email with your booking request details has been sent to you ({{ email }}) from global-support@wabunka-media.jp. Please check your inbox and spam or junk folder.</p>
    </div>

    <ul class="list-disc pl-6 leading-6 text-gray6">
      <li>Once your booking is confirmed, you will receive an email titled "Booking Confirmation."</li>
      <li>If you do not receive any email, please contact us via the inquiry form or at global-support@wabunka-media.jp.</li>
      <li>Additional charges may apply for optional add-ons.</li>
      <li>
        Please note that once the cancellation policy comes into effect, changes to your booking (including date, time, number of participants, and options) as well as cancellations may no longer be
        possible.
      </li>
    </ul>
  </div>

  <!-- 即時予約 -->
  <div v-else class="bg-white p-4 text-sm md:px-10 md:py-8 md:text-base">
    <div class="mb-2 flex flex-row items-center gap-x-2 leading-6 md:leading-8">
      <p class="text-[18px] text-gold3 md:text-[22px]">Booking Confirmed</p>
    </div>
    <p class="mask-me mt-5 leading-5 md:leading-6">Your booking is confirmed. An email has been sent to you ({{ email }}) with your experience details from global-support@wabunka-media.jp.</p>
    <ul class="mt-5 list-disc pl-7 leading-5 md:leading-6">
      <li>Please check your email for confirmation and further instructions.</li>
      <li>If you do not receive an email, please contact us via the inquiry form.</li>
      <li>Additional charges may apply for schedule changes or optional add-ons.</li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { WabunkaExperienceEventReservationType } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_experience_event_reservation_type';

interface Props {
  email: string;
  reservationType?: WabunkaExperienceEventReservationType;
}

const props = defineProps<Props>();

const isRequestReservation = computed(() => props.reservationType !== WabunkaExperienceEventReservationType.EXPERIENCE_EVENT_RESERVATION_TYPE_INSTANT);
</script>

<style scoped lang="scss">
.reservation-wizard {
  &__list {
    width: 100%;
    position: relative;
    text-align: center;

    &:nth-of-type(n + 1)::before {
      content: '';
      display: block;
      width: 100%;
      position: absolute;
      top: 0;
      left: -50%;
      border-top-width: 1px;
    }

    &::after {
      content: '';
      display: block;
      width: 16px;
      height: 16px;
      position: absolute;
      top: -8px;
      left: calc(50% - 8px);
      background-image: url('@/assets/img/parts/progress-bar-sleep.svg');
      background-size: contain;
      background-repeat: no-repeat;
      z-index: 1;
    }

    &:first-child::before {
      width: 50%;
      left: 0;
    }

    &--active {
      &::after {
        background-image: url('@/assets/img/parts/progress-bar-active.svg');
      }
    }
  }
}
</style>
